import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Send, MessageSquareText } from "@/components/icons";
import LikeButton from "@/components/LikeButton";

export default function OrientacaoThread({ clienteId, ownerId }) {
  const { user } = useAuth();
  const isManager = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  const isOwnerSeller = user?.id === ownerId;
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clientes/${clienteId}/orientacoes`);
      setItems(data.items || []);
    } catch { /* ignore */ }
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/clientes/${clienteId}/orientacoes`, { text: text.trim() });
      setText("");
      toast.success(isManager && !isOwnerSeller ? "Orientação enviada ao vendedor" : "Mensagem enviada");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSending(false); }
  };

  const composerLabel = isManager && !isOwnerSeller ? "Enviar orientação ao vendedor" : "Responder ao gestor";
  const placeholder = isManager && !isOwnerSeller
    ? "Ex.: Este cliente está há 18 dias sem atividade. Favor realizar contato e registrar a próxima ação."
    : "Escreva sua resposta ao gestor...";

  return (
    <div>
      <div className="space-y-3 mb-4" data-testid="orientacao-lista">
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center flex flex-col items-center gap-2">
            <MessageSquareText className="w-8 h-8 text-slate-300" />
            Nenhuma orientação neste cliente ainda.
          </div>
        ) : (
          items.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`orientacao-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-brand text-white" : "bg-slate-100 text-navy"}`}>
                  <div className={`text-[11px] font-semibold ${mine ? "text-white/80" : "text-slate-500"}`}>{m.sender_name}</div>
                  <div className="text-sm leading-snug mt-0.5 whitespace-pre-wrap">{m.text}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-slate-400"}`}>{fmtDateTime(m.created_at)}{mine && m.read ? " · lida" : ""}</div>
                </div>
                <LikeButton entityType="orientacao" entityId={m.id} authorId={m.sender_id} clienteId={clienteId}
                  label={m.text?.slice(0, 60)} count={m.likes_count || 0} liked={!!m.liked} />
              </div>
            );
          })
        )}
      </div>
      <div className="border-t border-black/5 pt-4">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">{composerLabel}</label>
        <div className="flex gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={placeholder} data-testid="orientacao-input"
            className="flex-1 px-3 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 resize-none" />
          <button onClick={send} disabled={sending || !text.trim()} data-testid="orientacao-send"
            className="h-11 px-4 self-end rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-50 flex items-center gap-2">
            <Send className="w-4 h-4" /> Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
